/** First non-empty trimmed string; supports alternate API field names. */
function coalesceTrim(...parts) {
  for (const p of parts) {
    if (p == null) {
      continue;
    }
    const t = String(p).trim();
    if (t) {
      return t;
    }
  }
  return "";
}

/**
 * Plan details line-items set-filter token for one row (must match filter dropdown values).
 * Whole-department rows use `D:{deptId}`; sub-department rows use `S:{deptId}:{mappingId}` only.
 */
export function planDetailsDepartmentFilterToken(row) {
  if (!row || row.departmentId == null) {
    return "";
  }
  if (row.departmentMappingId != null && row.departmentMappingId !== "") {
    return `S:${row.departmentId}:${row.departmentMappingId}`;
  }
  return `D:${row.departmentId}`;
}

/** Values passed to AG Grid set filter (one token per row). */
export function planDetailsDepartmentFilterTokens(row) {
  const token = planDetailsDepartmentFilterToken(row);
  return token ? [token] : [];
}

/**
 * When rollup (or API) selects a parent `D:{deptId}`, include all sub `S:` tokens for that dept.
 * Column filter picks a single `D:` checkbox — do not call this for manual set-filter changes.
 */
export function expandParentDepartmentFilterTokens(selectedTokens, departmentPlanRows) {
  if (!Array.isArray(selectedTokens) || selectedTokens.length === 0) {
    return selectedTokens ?? [];
  }
  const expanded = new Set(selectedTokens);
  for (const token of selectedTokens) {
    if (typeof token !== "string" || !token.startsWith("D:")) {
      continue;
    }
    const deptId = token.slice(2);
    for (const r of departmentPlanRows ?? []) {
      if (r?.departmentId == null || String(r.departmentId) !== String(deptId)) {
        continue;
      }
      const rowToken = planDetailsDepartmentFilterToken(r);
      if (rowToken) {
        expanded.add(rowToken);
      }
    }
  }
  return [...expanded];
}

export function departmentRowDisplayName(row) {
  if (!row) {
    return "";
  }
  if (row.id === "select-all") {
    return coalesceTrim(row.departmentName, row.name) || "";
  }
  const sub = coalesceTrim(row.subDepartmentName, row.subDepartment);
  const dept = coalesceTrim(row.departmentName, row.name, row.departmentLabel);
  return sub || dept;
}

/**
 * Full label: "Parent — Sub" when both exist (e.g. long-form tooltips that are not next to a whole-dept row).
 */
export function departmentSubRowTooltipLabelFromPlanRow(r) {
  if (!r) {
    return "";
  }
  const dept = coalesceTrim(r.departmentName, r.name, r.departmentLabel);
  const sub = coalesceTrim(r.subDepartmentName, r.subDepartment);
  if (dept && sub) {
    return `${dept} — ${sub}`;
  }
  return departmentRowDisplayName(r);
}

/**
 * Label for a sub-department mapping in POD/POW value breakdowns (Edit Value tooltips) when a
 * whole-department line and a sub line both appear: use the sub name so rows stay distinct
 * (e.g. "Food and Beverages" vs "Starbucks", not two lines both starting with the parent name).
 */
export function departmentMappingBreakdownLabelFromPlanRow(r) {
  if (!r) {
    return "";
  }
  const sub = coalesceTrim(r.subDepartmentName, r.subDepartment);
  if (sub) {
    return sub;
  }
  const dept = coalesceTrim(r.departmentName, r.name, r.departmentLabel);
  if (dept) {
    return dept;
  }
  return departmentRowDisplayName(r);
}

/**
 * departmentId -> parent department name from plan rows.
 * Fills whole-dept POD value keys when the flat departments list is missing a row or uses `departmentId` instead of `id`.
 */
export function departmentIdLabelsFromPlanRows(rows) {
  const map = {};
  (rows || []).forEach((r) => {
    if (r?.departmentId == null) {
      return;
    }
    const name = coalesceTrim(r.departmentName, r.name, r.departmentLabel);
    if (!name) {
      return;
    }
    const id = r.departmentId;
    map[id] = name;
    map[String(id)] = name;
    if (Number.isFinite(Number(id))) {
      map[Number(id)] = name;
    }
  });
  return map;
}

/** departmentMappingId -> label for Edit Value POD/POW breakdown / selection (sub name when present). */
export function departmentMappingLabelsFromPlanRows(rows) {
  const map = {};
  (rows || []).forEach((r) => {
    if (r?.departmentMappingId == null) {
      return;
    }
    const label = departmentMappingBreakdownLabelFromPlanRow(r);
    if (label) {
      const id = r.departmentMappingId;
      map[id] = label;
      map[String(id)] = label;
      if (Number.isFinite(Number(id))) {
        map[Number(id)] = label;
      }
    }
  });
  return map;
}

export function uniqueFlatDepartmentsFromPlanRows(rows) {
  const byId = new Map();
  (rows || []).forEach((r) => {
    if (r?.departmentId == null) {
      return;
    }
    if (!byId.has(r.departmentId)) {
      byId.set(r.departmentId, {
        id: r.departmentId,
        departmentName: coalesceTrim(r.departmentName, r.name) || "",
      });
    }
  });
  return Array.from(byId.values()).sort((a, b) =>
    (a.departmentName || "").localeCompare(b.departmentName || "", undefined, {
      sensitivity: "base",
    })
  );
}

export function mapPlanRowsToPodPowSelectOptions(rows) {
  const sorted = [...(rows || [])].sort((a, b) =>
    departmentRowDisplayName(a).localeCompare(departmentRowDisplayName(b), undefined, {
      sensitivity: "base",
    })
  );
  return sorted.map((r) => {
    const subName = coalesceTrim(r.subDepartmentName, r.subDepartment);
    const hasMap =
      r.departmentMappingId != null && r.departmentMappingId !== "" && !!subName;
    return {
      id: hasMap ? `map:${r.departmentMappingId}` : `dept:${r.departmentId}`,
      optionKind: hasMap ? "sub" : "dept",
      departmentId: r.departmentId,
      departmentMappingId: hasMap ? r.departmentMappingId : null,
      departmentName: r.departmentName,
      subDepartmentName: hasMap ? subName : "",
    };
  });
}

/** Build multiselect options from prefetch cache (plan rows with sub-departments, or flat departments). */
export function planDepartmentOptionsFromCache(cachedData) {
  const rows = cachedData?.departmentPlanRows;
  if (rows && Array.isArray(rows) && rows.length > 0) {
    return mapPlanRowsToPodPowSelectOptions(rows);
  }
  const depts = cachedData?.departments || [];
  return depts.map((d) => ({
    id: `dept:${d.id ?? d.departmentId}`,
    optionKind: "dept",
    departmentId: d.id ?? d.departmentId,
    departmentMappingId: null,
    departmentName: d.departmentName ?? d.name ?? "",
    subDepartmentName: "",
  }));
}

/** Restore multiselect values from driverConfig / import selection. */
export function matchSavedDepartmentSelections(options, departmentIds, departmentMappingIds) {
  const dset = new Set((departmentIds || []).map((x) => Number(x)));
  const mset = new Set((departmentMappingIds || []).map((x) => Number(x)));
  return (options || []).filter((o) => {
    if (o.optionKind === "sub" && o.departmentMappingId != null) {
      return mset.has(Number(o.departmentMappingId));
    }
    if (o.optionKind === "dept" && o.departmentId != null) {
      return dset.has(Number(o.departmentId));
    }
    return false;
  });
}

/** Persist POD/POW/import/driver multiselect into driverConfig / API fields. */
export function driverConfigDepartmentPayloadFromOptions(selectedOptions) {
  const departmentIds = [];
  const departmentMappingIds = [];
  (selectedOptions || []).forEach((o) => {
    if (o.optionKind === "sub" && o.departmentMappingId != null) {
      departmentMappingIds.push(Number(o.departmentMappingId));
    } else if (o.optionKind === "dept" && o.departmentId != null) {
      departmentIds.push(Number(o.departmentId));
    }
  });
  return {
    departmentIds: [...new Set(departmentIds)],
    departmentMappingIds: [...new Set(departmentMappingIds)],
  };
}

/** Parent department ids for structure-code / GL pairing (whole dept + sub rows). */
export function departmentNumericIdsForStructureFilter(departmentIds, departmentMappingIds, allOptions) {
  const d = new Set((departmentIds || []).map((x) => Number(x)));
  const mids = new Set((departmentMappingIds || []).map((x) => Number(x)));
  (allOptions || []).forEach((o) => {
    if (
      o.optionKind === "sub" &&
      o.departmentMappingId != null &&
      mids.has(Number(o.departmentMappingId)) &&
      o.departmentId != null
    ) {
      d.add(Number(o.departmentId));
    }
  });
  return Array.from(d);
}

/**
 * Resolve `id` -> value from maps keyed by id (string/number keys from API).
 * Returns undefined if not found (unlike `labelFromIdLookupMap`, no fallback to the raw key).
 */
export function valueFromIdLookupMap(dataMap, key) {
  if (dataMap == null || key == null) {
    return undefined;
  }
  if (dataMap instanceof Map) {
    return (
      dataMap.get(key) ??
      dataMap.get(String(key)) ??
      (Number.isFinite(Number(key)) ? dataMap.get(Number(key)) : undefined)
    );
  }
  return (
    dataMap[key] ??
    dataMap[String(key)] ??
    (Number.isFinite(Number(key)) ? dataMap[Number(key)] : undefined)
  );
}

/**
 * Label map for POD/POW (driver 5/8/22/23) value-breakdown keys, scoped to the driver's own selection.
 * A breakdown key is a `departmentMappingId` when a sub-department was selected, else a `departmentId`
 * (backend `percentOfDepartmentBreakdownKey`). Department ids and mapping ids come from separate tables
 * and can share a number, so blindly merging the two full label maps resolves some keys to an unrelated
 * department's name. Populating only the selected ids — mapping label for a mapping selection, department
 * label for a department selection — removes that collision.
 */
export function departmentBreakdownLabelMapFromSelection(cfg, departmentDataMap, departmentMappingLabelsMap) {
  const map = {};
  const addKeys = (ids, source) => {
    (ids || []).forEach((id) => {
      const label = valueFromIdLookupMap(source, id);
      if (label == null) {
        return;
      }
      map[id] = label;
      map[String(id)] = label;
      if (Number.isFinite(Number(id))) {
        map[Number(id)] = label;
      }
    });
  };
  addKeys(cfg?.departmentIds, departmentDataMap);
  addKeys(cfg?.departmentMappingIds, departmentMappingLabelsMap);
  return map;
}

/** Resolve `id` -> label from maps keyed by id (string/number keys from API). */
export function labelFromIdLookupMap(dataMap, key) {
  if (dataMap == null || key == null) {
    return String(key);
  }
  return valueFromIdLookupMap(dataMap, key) ?? key;
}

/**
 * Chart of Accounts: `all-structure-code-with-account-structure-mappings` returns
 * `DepartmentSubDepartmentDTO` rows (department + optional sub-department).
 */
export function coaDepartmentComboOptionsFromApi(departments) {
  if (!Array.isArray(departments) || departments.length === 0) return [];
  return departments.map((item) => {
    const departmentId = item.departmentId;
    const departmentMappingId =
      item.departmentMappingId != null && item.departmentMappingId !== ""
        ? item.departmentMappingId
        : null;
    const departmentName = coalesceTrim(item.departmentName, item.name, item.departmentLabel);
    const sub = coalesceTrim(item.subDepartmentName, item.subDepartment);
    // List UI: sub-department rows show only the sub name; parent rows (no mapping id / no sub) show department name.
    const label = sub || departmentName || String(departmentId ?? "");
    const optionKey =
      departmentMappingId != null && departmentMappingId !== ""
        ? `m:${departmentMappingId}`
        : `d:${departmentId}`;
    return { departmentId, departmentMappingId, label, optionKey, departmentName };
  });
}

/** Parent department name per id (for structure-code hints, filters, sorting). */
export function coaDepartmentsIdNameMapFromCombo(comboOptions) {
  const acc = {};
  (comboOptions || []).forEach((o) => {
    if (o.departmentId == null) return;
    const id = o.departmentId;
    if (acc[id] !== undefined) return;
    const parent =
      o.departmentName != null && String(o.departmentName).trim() !== ""
        ? String(o.departmentName).trim()
        : (o.label || "").trim() || String(id);
    acc[id] = parent;
    acc[String(id)] = parent;
    if (Number.isFinite(Number(id))) {
      acc[Number(id)] = parent;
    }
  });
  return acc;
}

export function filterCoaDepartmentComboByDeptIds(comboOptions, deptIdList) {
  if (!comboOptions?.length || !deptIdList?.length) return [];
  const idSet = new Set(deptIdList.map((id) => String(id)));
  return comboOptions.filter((o) => idSet.has(String(o.departmentId)));
}

/** Match row `departmentId` / `departmentMappingId` to a combo option (filtered or full list). */
export function resolveCoaDepartmentComboValue(rowData, optionList) {
  if (!optionList?.length) return null;
  const mid = rowData?.departmentMappingId;
  if (mid != null && mid !== "") {
    const byMap = optionList.find(
      (o) => o.departmentMappingId != null && String(o.departmentMappingId) === String(mid)
    );
    if (byMap) return byMap;
  }
  const did = rowData?.departmentId;
  if (did == null || did === "") return null;
  const deptOnly = optionList.find(
    (o) =>
      String(o.departmentId) === String(did) &&
      (o.departmentMappingId == null || o.departmentMappingId === "")
  );
  if (deptOnly) return deptOnly;
  // Row has parent `departmentId` only (no `departmentMappingId`). Do not match a sub-row
  // by `departmentId` alone — that would show the first sub as selected. User must pick a sub.
  return null;
}

/**
 * COA grid set-filter / sort value: encodes parent-only vs sub-department (matches `optionKey` on combo options).
 */
export function coaRowDepartmentFilterToken(rowData) {
  if (!rowData) {
    return "";
  }
  const mid = rowData.departmentMappingId;
  if (mid != null && mid !== "") {
    return `m:${mid}`;
  }
  const did = rowData.departmentId;
  if (did != null && did !== "") {
    return `d:${did}`;
  }
  return "";
}

/** Resolve a filter token to a display label using the same list as the Department Autocomplete. */
export function coaDepartmentLabelForFilterToken(token, optionList) {
  if (token == null || token === "") {
    return "";
  }
  const t = String(token);
  if (t.startsWith("DIVIDER")) {
    return "";
  }
  const o = (optionList || []).find((x) => x.optionKey === t);
  return o ? String(o.label || "").trim() : "";
}